import { converters as tableConverters } from 'store/table';
import { converters as filesConverters } from 'store/files';
import { IDiscount, IDiscountWithStats } from 'types/income';
import {
  discountAmountFromApi,
  discountOfferTypeFromApi,
} from './discount-offer';
import { loaders } from './discounts.loaders';
import { Discount, DiscountWithStats } from './discounts.types';

const convertDiscount = (discount: IDiscount): Discount => {
  return {
    id: discount.id,
    name: discount.name,
    supplier: discount.supplier
      ? {
          id: discount.supplier.id,
          name: discount.supplier.name,
          logo: discount.supplier.logo
            ? filesConverters.getUploadedFile(discount.supplier.logo)
            : null,
          contact: discount.supplierContact ?? '',
        }
      : null,
    priority: discount.priority,
    status: discount.status,
    isEnabled: discount.isEnabled,
    image: filesConverters.getUploadedFile(discount.discountImage),
    translations: discount.translations.map((trans) => ({
      language: trans.language,
      title: trans.discountTitle,
      teaser: trans.teaserLine,
      description: trans.discountDescription,
      promo: trans.discountPromo,
      attachments: [],
    })),
    countries: discount.countries,
    cities: discount.cities,
    categories: discount.categories,
    validFrom: discount.validFrom,
    validTo: discount.validTo,
    publishedAt: discount.publishDateTime,
    type: discountOfferTypeFromApi(discount.type),
    discountedPercentage: discountAmountFromApi(discount.discountedPercentage),
    discountedFixed: discountAmountFromApi(discount.discountedFixed),
    currency: discount.currency ?? null,
    averageBasketSize: discountAmountFromApi(discount.averageBasketSize),
    averageSaving: discountAmountFromApi(discount.averageSaving),
  };
};

const convertDiscounts = (discounts: IDiscount[]): Discount[] => {
  return discounts.map(convertDiscount);
};

const convertDiscountWithStats = (
  discount: IDiscountWithStats,
): DiscountWithStats => {
  return {
    id: discount.id,
    name: discount.title,
    supplier: discount.supplierId
      ? {
          id: discount.supplierId,
          name: discount.supplierName,
          logo: null,
          contact: '',
        }
      : null,
    packages: discount.discountPackages,
    validFrom: discount.discountValidFrom,
    validTo: discount.discountValidTo,
    favoritesCount: discount.isFavouriteCount,
    viewsCount: discount.views,
    uniqueViewsCount: discount.uniqueViews,
    isUniqueCode: discount.isUniqueCode,
    isEnabled: discount.isEnabled,
  };
};

const convertDiscountsWithStats = (
  discounts: IDiscountWithStats[],
): DiscountWithStats[] => {
  return discounts.map(convertDiscountWithStats);
};

export const converters = {
  getDiscounts: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getDiscounts>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertDiscounts(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getDiscountsWithStats: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getDiscountsWithStats>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertDiscountsWithStats(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
};
